import { Divider, type RefSelectProps } from 'antd'
import clsx from 'clsx'
import { useAtomValue } from 'jotai'
import {
  type Ref,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { FaPlus, FaSyncAlt } from 'react-icons/fa'

import { Button, Select } from '@/components/elements'
import { type SingleOrMultipleSelectProps } from '@/components/types'
import {
  ACTIONS,
  DESCRIPTIONS,
  ERROR_MESSAGES
} from '@/constants/common/message.constants'
import { fetchPartnerDetail } from '@/features/partners/api'
import {
  type PartnerToSubPartnerEntity,
  type SubPartnerEntity
} from '@/libs/api'
import { useSelectOnChangeHandler } from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { SubPartnerCreateModal } from '../components/SubPartnerCreateModal'
import { useSubPartnersSelectQuery } from '../hooks'

type Props = SingleOrMultipleSelectProps<number, SubPartnerEntity> & {
  tenantKey: string
  dcId?: number
  partnerId?: number
  enable?: boolean
  onCreate?: (site: SubPartnerEntity) => void
  showPartnerName?: boolean
}

export const SubPartnerSelect = forwardRef(
  (
    {
      className,
      placeholder = DESCRIPTIONS.SEARCHING_SUB_PARTNERS,
      mode,
      onChange,
      tenantKey,
      dcId,
      enable,
      value,
      partnerId,
      onCreate,
      showPartnerName,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const selectedDcId = useAtomValue(selectedDcIdAtom)
    const { data, isFetching, isLoading, refetch } = useSubPartnersSelectQuery({
      tenantKey,
      query: {
        partnerId,
        dcId: dcId ?? selectedDcId,
        enable
      }
    })
    const [isOpenCreateModal, setIsOpenCreateModal] = useState<boolean>(false)

    const handleChange = useSelectOnChangeHandler({ mode, onChange })

    const wrappedHandleChange = (
      selectedId: number | number[] | null,
      option: SubPartnerEntity | SubPartnerEntity[] | null
    ) => {
      if (mode === 'multiple') {
        const ids = Array.isArray(selectedId)
          ? selectedId
              .map((id) => (id === -1 ? -1 : id))
              .filter((id): id is number => id !== null)
          : []
        handleChange(ids, option)
      } else {
        handleChange(selectedId, option) // -1もそのまま渡す
      }
    }

    // partner情報まとめて管理
    const [partnerInfo, setPartnerInfo] = useState<{
      name: string
      companyId: number
    }>({ name: '', companyId: 0 })

    useEffect(() => {
      if (!showPartnerName || !partnerId) {
        setPartnerInfo({ name: '', companyId: 0 })
        return
      }
      fetchPartnerDetail({ tenantKey, partnerId })
        .then((partner) =>
          setPartnerInfo({
            name: partner?.name ?? '',
            companyId: partner?.companyId ?? 0
          })
        )
        .catch(() => setPartnerInfo({ name: '', companyId: 0 }))
    }, [showPartnerName, partnerId, tenantKey])

    // 実運送会社の重複を排除 (パートナーによる絞り込みがされていない場合のみ)
    const filteredData = useMemo(() => {
      if (!data) return []

      if (partnerId) return data.map(({ subPartner }) => subPartner)

      // partnerId がない場合は id の重複を排除
      const seen = new Set<number>()
      const result: SubPartnerEntity[] = []

      for (const { subPartner } of data) {
        if (!seen.has(subPartner.id)) {
          seen.add(subPartner.id)
          result.push(subPartner)
        }
      }

      return result
    }, [data, partnerId])

    // サブパートナー選択肢
    const subPartnerData: SubPartnerEntity[] = [
      ...(showPartnerName && partnerInfo.name
        ? [
            {
              id: -1,
              name: partnerInfo.name,
              companyId: partnerInfo.companyId,
              dcId: dcId ?? 0,
              enable: true,
              createdAt: '',
              updatedAt: '',
              createdUserId: 0,
              updatedUserId: 0
            }
          ]
        : []),
      ...filteredData
    ]

    // valibotのバリデーションによって初期値をnullにできない場合に、プルダウンに「0」などの初期値が表示されないように
    const selectedValue =
      mode === 'multiple'
        ? Array.isArray(value)
          ? value.filter((val) =>
              subPartnerData.some((item) => item.id === val)
            )
          : []
        : value === -1 || subPartnerData.some((item) => item.id === value)
          ? value
          : null

    const handleCreateSuccess = useCallback(
      async ({ subPartner }: PartnerToSubPartnerEntity) => {
        await refetch()
        handleChange(subPartner.id, subPartner)
        setIsOpenCreateModal(false)
        if (onCreate) onCreate(subPartner)
      },
      [handleChange, onCreate, refetch]
    )

    return (
      <>
        <Select
          customRef={ref}
          mode={mode}
          value={isLoading ? null : selectedValue}
          placeholder={placeholder}
          fieldNames={{ label: 'name', value: 'id' }}
          className={clsx(['h-[32px]', className])}
          options={subPartnerData}
          optionFilterProp="name"
          filterOption={(input, option) =>
            (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
          }
          showSearch
          loading={isFetching}
          onChange={wrappedHandleChange}
          notFoundContent={
            isFetching ? (
              <div className="flex items-center justify-center p-2">
                <FaSyncAlt className="mr-2 animate-spin" />
                <span>{DESCRIPTIONS.SEARCHING}</span>
              </div>
            ) : (
              <div className="p-2 text-center text-gray-500">
                {ERROR_MESSAGES.SUB_PARTNER_NOT_FOUND}
              </div>
            )
          }
          allowClear
          {...props}
          dropdownRender={
            onCreate
              ? (menu) => (
                  <div>
                    {menu}
                    <Divider className="mx-0 my-2" />
                    <div className="text-center">
                      <Button
                        disabled={!partnerId}
                        icon={<FaPlus />}
                        onClick={() => setIsOpenCreateModal(true)}
                      >
                        {ACTIONS.CREATE_ACTUAL_SUB_PARTNER}
                      </Button>
                    </div>
                  </div>
                )
              : undefined
          }
        />
        <SubPartnerCreateModal
          open={isOpenCreateModal}
          tenantKey={tenantKey}
          onSuccess={handleCreateSuccess}
          onClose={() => setIsOpenCreateModal(false)}
          partnerId={partnerId!}
        />
      </>
    )
  }
)
